import i18next from 'i18next';

/**
 * Alert criticality buckets. Backend string values — preserved
 * verbatim from legacy `_legacy/components/Alerts/constants.js`.
 */
export const ALERTS_TYPE = {
  critical: 'Critical',
  nonCritical: 'Non Critical',
  'monitor & support': 'monitor & support',
};

/**
 * Source entity types that can produce alerts.
 */
export const ENTITY_SOURCE_TYPE = {
  program: 'Program',
  assessment: 'Assessment',
  resource: 'Resource',
  // The DISPLAY label of the `intellyChart` entity type (see
  // CLAIM_ENTITY_TYPE_SELECTOR_OPTIONS) — the badge tone is looked up by
  // it, so it has to follow the client rename.
  intellyChart: 'Preventive Health Counseling',
  bhiWebhook: 'BHI Webhook',
};

/**
 * Display-ready options for the alertType filter.
 */
export const ALERTS_TYPE_SELECTOR_OPTIONS = [
  { label: i18next.t('tables:critical'), value: 'critical' },
  { label: i18next.t('tables:nonCritical'), value: 'nonCritical' },
  { label: i18next.t('tables:monitor&support'), value: 'monitor & support' },
];

/**
 * Display-ready options for the acknowledgeStatus filter.
 */
export const STATUS_OPTIONS = [
  { label: i18next.t('tables:pendingAcknowledgment'), value: 'pendingAcknowledgment' },
  { label: i18next.t('tables:acknowledged'), value: 'acknowledged' },
  { label: i18next.t('tables:pending'), value: 'pending' },
];

/**
 * Filter options for the "Triggered" column (OI-CDS-04).
 *
 * The API reads the term as a boolean, so the values are the literal
 * strings it parses. Its answer is three-state and "No" is the narrow
 * one: alerts whose assessment SHOWED the crisis resources and whose
 * patient did not tap them. Alerts that never showed them (the dash in
 * the cell) are not an answer to that question and stay out of "No" —
 * otherwise "No" would be mostly irrelevant rows.
 *
 * Picking both options means "has an answer": the API returns every
 * alert whose assessment showed the resources, tapped or not, and
 * leaves the not-applicable ones out.
 */
export const CRISIS_TRIGGERED_OPTIONS = [
  { label: i18next.t('tables:crisisTriggeredYes'), value: 'true' },
  { label: i18next.t('tables:crisisTriggeredNo'), value: 'false' },
];

/**
 * Alert category labels (backend string values, used in dashboards
 * downstream — preserved verbatim from legacy).
 */
export const ALERT_CATEGORY_STATUSES = {
  'Imminent Risk': 'Imminent Risk',
  'Help Seeking Behavior': 'Help Seeking Behavior',
  'Risk Factors Identified': 'Risk Factors Identified',
};

/**
 * Static fallback for the `alertTags` column when the backend
 * `/accounts/alerts/tags` options endpoint hasn't yet caught up
 * with newly-introduced taxonomy (PHQ-GAD16 / Q-Lung). Merged
 * with backend-supplied options in
 * `@shared/ui/Table/lib/helpers.js`'s `case 'alertTags'` (dedup
 * by `value`, backend wins on collision). Idempotent once the
 * endpoint catches up.
 *
 * Frontend parity with dev commit d19d37e9 (branch
 * `fix/dev-alert-tags-and-select-all`).
 */
export const ALERT_TAGS_OPTIONS = [
  // ---- Risk Factors Identified ----
  { value: 'Q-NEG Positive', label: 'Q-NEG Positive' },
  { value: 'Depression risk detected', label: 'Depression risk detected' },
  { value: 'Anxiety risk detected', label: 'Anxiety risk detected' },
  { value: 'Elevated Allergy Score', label: 'Elevated Allergy Score' },
  { value: 'Poor Asthma Control', label: 'Poor Asthma Control' },
  { value: 'Symptomatic COPD impact score', label: 'Symptomatic COPD impact score' },
  { value: 'High Sleep Disorder Risk', label: 'High Sleep Disorder Risk' },
  { value: 'Tobacco Use', label: 'Tobacco Use' },
  { value: 'Positive PSC17', label: 'Positive PSC17' },
  {
    value: 'Depression and/ or Anxiety risk detected',
    label: 'Depression and/ or Anxiety risk detected',
  },
  { value: 'ASCVD Risk', label: 'ASCVD Risk' },
  { value: 'CAD Risk', label: 'CAD Risk' },
  { value: 'PAD Risk', label: 'PAD Risk' },
  { value: 'Dementia Risk Positive', label: 'Dementia Risk Positive' },
  { value: 'Cognitive Screen Positive', label: 'Cognitive Screen Positive' },
  { value: 'Obesity (BMI ≥30 kg/m²)', label: 'Obesity (BMI ≥30 kg/m²)' },
  {
    value: 'Muscle-strengthening exercise painful or too difficult',
    label: 'Muscle-strengthening exercise painful or too difficult',
  },
  {
    value: 'Physical limitation to strength training',
    label: 'Physical limitation to strength training',
  },
  // Weight & Wellness Weekly Check-In (API 2026-10-30)
  {
    value: 'Weight management safety symptoms reported',
    label: 'Weight management safety symptoms reported',
  },
  {
    value: 'Significant weight management medication side effects',
    label: 'Significant weight management medication side effects',
  },
  {
    value: 'Weight management medication adherence concern',
    label: 'Weight management medication adherence concern',
  },
  {
    value: 'Weight gain on weight management therapy',
    label: 'Weight gain on weight management therapy',
  },
  { value: 'Weight loss plateau', label: 'Weight loss plateau' },
  // Q-Nutrition (API 2026-10-30)
  {
    value: 'Vegetarian or vegan without a reported B12 source',
    label: 'Vegetarian or vegan without a reported B12 source',
  },
  { value: 'Unable to keep food or fluids down', label: 'Unable to keep food or fluids down' },
  // ---- Help Seeking Behavior ----
  { value: 'Tobacco cessation help requested', label: 'Tobacco cessation help requested' },
  { value: 'Mental Health Help Requested', label: 'Mental Health Help Requested' },
  { value: 'Sleep Help Requested', label: 'Sleep Help Requested' },
  { value: 'Allergy Test Requested', label: 'Allergy Test Requested' },
  { value: 'Interested in Immunotherapy', label: 'Interested in Immunotherapy' },
  { value: 'Lifestyle Help Requested', label: 'Lifestyle Help Requested' },
  { value: 'GLP-1/GIP Treatment Interest', label: 'GLP-1/GIP Treatment Interest' },
  { value: 'Care team contact requested', label: 'Care team contact requested' },
  { value: 'BHI Negative', label: 'BHI Negative' },
  // ---- Historical / legacy tags (preserve back-compat) ----
  { value: 'Imminent Risk', label: 'Imminent Risk' },
  { value: 'Suicide or Self-Harm Risk', label: 'Suicide or Self-Harm Risk' },
  {
    value: 'Psychological distress, no active suicidal ideation',
    label: 'Psychological distress, no active suicidal ideation',
  },
  { value: 'Persistent Pain', label: 'Persistent Pain' },
  { value: 'Significant Physical Limitations', label: 'Significant Physical Limitations' },
  { value: 'SDoH Risk', label: 'SDoH Risk' },
  { value: 'Personal Safety Risk', label: 'Personal Safety Risk' },
  { value: 'Preventive Screening Required', label: 'Preventive Screening Required' },
  { value: 'Check A1C', label: 'Check A1C' },
  { value: 'Fall risk', label: 'Fall risk' },
  { value: 'Mental Health', label: 'Mental Health' },
  { value: 'Pain Management', label: 'Pain Management' },
  { value: 'Sleep Quality', label: 'Sleep Quality' },
  { value: 'Lifestyle Improvement', label: 'Lifestyle Improvement' },
  { value: 'Home Sleep Study', label: 'Home Sleep Study' },
  { value: 'Sleep Study Requested', label: 'Sleep Study Requested' },
  { value: 'Pain Management Help Requested', label: 'Pain Management Help Requested' },
  { value: 'Allergy Test', label: 'Allergy Test' },
  { value: 'BHI Consent', label: 'BHI Consent' },
];
